import type { Options } from "../../types";
import { isNoCoverValue } from "./constants";

// Returns the "no cover" value from options (first match from NO_COVER_VALUES, defaults to 'no')
export const getOffValue = (options: Options | undefined): string => {
  if (!options || options.length === 0) return "no";
  const noCoverOption = options.find(opt => isNoCoverValue(opt.value));
  return noCoverOption?.value || "no";
};

// Returns default "on" value: non-off option for 2 options, first valid cover for 3+
export const getDefaultOnValue = (
  options: Options | undefined,
  offValue: string,
): string => {
  if (!options || options.length === 0) return "yes";

  if (options.length === 2) {
    return options.find(opt => opt.value !== offValue)?.value || "yes";
  }

  return options.find(opt => !isNoCoverValue(opt.value))?.value || "yes";
};

// Returns true if switch should be "on": value must be in options and not a "no cover" value
export const getIsChecked = (
  value: string | number | boolean | string[] | undefined,
  offValue: string,
  options: Options | undefined,
): boolean => {
  if (value === "" || value === undefined) return false;
  if (value === offValue) return false;
  if (!options || options.length === 0) return true;
  if (typeof value !== "string") return false;

  return options.some(opt => opt.value === value);
};

// Determines the initial value for the CoverLimitEditor - either from the provided value if valid or the first
// 'non-off' option in the options array.
export const getInitialSelectedValue = (
  value: string | number | boolean | string[] | undefined,
  options: Options | undefined,
  offValue: string,
): string => {
  // If value is a string, not empty, not the off value, and in options, use it
  if (
    typeof value === "string" &&
    value !== "" &&
    value !== offValue &&
    options?.some(opt => opt.value === value)
  ) {
    return value;
  }

  return getDefaultOnValue(options, offValue);
};

// Returns true if cover is mandatory (any number of options, no "off" option)
export const isMandatoryCover = (options: Options | undefined): boolean => {
  if (!options || options.length === 0) return false;

  return !options.some(opt => isNoCoverValue(opt.value));
};
